import type { KpiCadence, KpiMetric } from './kpiApi'
import { addDays, periodColumnLabel } from './grouping'

/**
 * The shape of the KPI grid — Ninety's scorecard: one row per measurable,
 * one column per trailing period, the current period rightmost.
 *
 * Columns are computed by DATE ARITHMETIC from the current period's start,
 * never by walking the history array. `list_kpi_metrics` returns the 12 most
 * recent ENTRIES, not the 12 most recent periods, so a metric with gaps
 * reaches back further than 12 periods and a metric with none has fewer —
 * aligning by array index would put numbers in the wrong week. Cells are
 * matched to columns by their `period_start` key instead.
 */

/** 12 prior periods + the current one, the same 13 columns Ninety shows. */
export const GRID_COLUMNS = 13

export interface GridColumn {
  /** YYYY-MM-DD: that day (daily) or that week's Monday (weekly). */
  start: string
  /** "Sep 28 – Oct 4" or "Oct 7". */
  label: string
  current: boolean
}

export function buildColumns(
  cadence: KpiCadence,
  currentPeriodStart: string,
  count = GRID_COLUMNS,
): GridColumn[] {
  const step = cadence === 'daily' ? 1 : 7
  const columns: GridColumn[] = []
  // Oldest first, so the current period lands rightmost.
  for (let i = count - 1; i >= 0; i--) {
    const start = addDays(currentPeriodStart, -i * step)
    columns.push({ start, label: periodColumnLabel(cadence, start), current: i === 0 })
  }
  return columns
}

export interface GridCell {
  value: number
  met: boolean
}

/**
 * Every entered period for one metric, keyed by `period_start`. Includes the
 * current period when it has a value. Entries older than the grid's window are
 * still in here — the grid simply never asks for those keys.
 */
export function cellMap(metric: KpiMetric): Map<string, GridCell> {
  const cells = new Map<string, GridCell>()
  for (const h of metric.history) {
    cells.set(h.period_start, { value: h.value, met: h.met })
  }
  if (metric.current_value != null) {
    cells.set(metric.period_start, {
      value: metric.current_value,
      met: metric.current_met ?? false,
    })
  }
  return cells
}

/**
 * The Avg column: the mean of what was entered inside the window. Empty cells
 * are left out rather than counted as zero — a week nobody logged is unknown,
 * not a miss. Returns null when nothing in the window has been entered.
 */
export function windowAverage(
  columns: GridColumn[],
  cells: Map<string, GridCell>,
): { average: number; entered: number; yes: number } | null {
  let sum = 0
  let entered = 0
  let yes = 0
  for (const col of columns) {
    const cell = cells.get(col.start)
    if (!cell) continue
    sum += cell.value
    entered += 1
    if (cell.value === 1) yes += 1
  }
  if (entered === 0) return null
  return { average: sum / entered, entered, yes }
}
